import React, { useContext, useEffect, useState } from 'react';
import GlobalContext from 'hooks/contexts/GlobalContext';
import { FORM_ANIMATION_SPEED } from 'consts';
import { LogWarning } from 'utils/logging';

interface CSSSettings {
  formSpeed: number;
  megaMenuAnimationSpeed: number;
  toolTipAnimationEnterSpeed: number;
  toolTipAnimationExitSpeed: number;
  toolTipViewPortOffset: number;
  headerHeight: number;
  faqPanelAnimationSpeed: number;
  carouselAnimationSpeed: number;
}

const SETTINGS_MAP: Record<
  keyof CSSSettings,
  { cssVar: string; default: number }
> = {
  faqPanelAnimationSpeed: { cssVar: '--faqPanelAnimationSpeed', default: 250 },
  formSpeed: { cssVar: '--formAnimationSpeed', default: FORM_ANIMATION_SPEED },
  headerHeight: { cssVar: '--headerHeight', default: 60 },
  megaMenuAnimationSpeed: { cssVar: '--megaMenuAnimationSpeed', default: 250 },
  toolTipAnimationEnterSpeed: {
    cssVar: '--toolTipAnimationEnterSpeed',
    default: 500,
  },
  toolTipAnimationExitSpeed: {
    cssVar: '--toolTipAnimationExitSpeed',
    default: 500,
  },
  toolTipViewPortOffset: { cssVar: '--toolTipViewPortOffset', default: 25 },
  carouselAnimationSpeed: { cssVar: '--carouselAnimationSpeed', default: 500 },
};

const DEFAULT_CSS_SETTINGS = Object.entries(SETTINGS_MAP).reduce<CSSSettings>(
  (acc, [key, value]) => ({
    ...acc,
    [key]: value.default,
  }),
  {} as CSSSettings
);

function calculateSettings(
  ref: React.RefObject<HTMLElement | null> | null
): CSSSettings {
  if (!ref?.current) {
    return DEFAULT_CSS_SETTINGS;
  }

  try {
    const styles = getComputedStyle(ref.current);
    return Object.entries(SETTINGS_MAP).reduce<CSSSettings>(
      (acc, [key, value]) => ({
        ...acc,
        [key]:
          parseInt(styles.getPropertyValue(value.cssVar), 10) || value.default,
      }),
      {} as CSSSettings
    );
  } catch (error) {
    LogWarning('Error calculating CSS settings, using defaults', {
      description: error,
    });
    return DEFAULT_CSS_SETTINGS;
  }
}

/**
 * Allows for shared variables between the CSS in an individual theme and a component.
 * Runs on breakpoint change
 * Returns an object of settings that are configured in the CSS.
 *
 * @param {React.node} scopeRef optional ref to use for the CSS Custom Property scope. If empty defaults to the themeWrapperRef
 * @return {Object}
 */
function useCSSsettings(
  scopeRef?: React.RefObject<HTMLElement | null>
): CSSSettings {
  const {
    windowSize: { currentBreakpoint },
    refs: { themeWrapperRef },
  } = useContext(GlobalContext);

  const currentRef = scopeRef || themeWrapperRef;
  const [settings, setSettings] = useState<CSSSettings>(() =>
    calculateSettings(currentRef)
  );

  useEffect(() => {
    setSettings(calculateSettings(currentRef));
  }, [currentBreakpoint, currentRef]);

  return settings;
}

export default useCSSsettings;
